import { useLayoutEffect, useRef, type ComponentPropsWithRef } from "react";

import { cn } from "../lib/cn";
import { controlBase } from "../lib/control";
import { useFieldControl } from "./field";

export interface TextareaProps extends ComponentPropsWithRef<"textarea"> {
  /** La zone grandit avec le contenu au lieu de faire défiler. */
  autoResize?: boolean;
  /** Hauteur maximale en lignes avant que le défilement ne reprenne. */
  maxRows?: number;
}

/** Zone de saisie multiligne. Redimensionnable verticalement uniquement. */
export function Textarea({
  className,
  rows = 4,
  autoResize = false,
  maxRows = 12,
  value,
  ...props
}: TextareaProps) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const fieldProps = useFieldControl(props);

  // Mesurer la hauteur du contenu impose de remettre `height` à `auto` d'abord :
  // sinon `scrollHeight` renvoie la hauteur actuelle et la zone ne rétrécit
  // jamais. En layout effect pour éviter un scintillement d'une frame.
  useLayoutEffect(() => {
    const element = ref.current;
    if (!autoResize || !element) return;

    element.style.height = "auto";
    const lineHeight = parseFloat(getComputedStyle(element).lineHeight) || 20;
    const max = lineHeight * maxRows;

    element.style.height = `${Math.min(element.scrollHeight, max)}px`;
    element.style.overflowY = element.scrollHeight > max ? "auto" : "hidden";
  }, [autoResize, maxRows, value, props.defaultValue]);

  return (
    <textarea
      ref={ref}
      rows={rows}
      value={value}
      // `resize-y` : autoriser le redimensionnement horizontal casserait
      // systématiquement la grille du formulaire. En mode auto, on le coupe
      // tout à fait — la hauteur est déjà pilotée par le contenu.
      className={cn(
        controlBase,
        "min-h-20 px-3 py-2 text-sm",
        autoResize ? "resize-none" : "resize-y",
        className,
      )}
      {...fieldProps}
    />
  );
}
